import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { BadgeCheck, BookOpenText, Copy, Globe, Highlighter, Loader2, MessageSquareQuote, X, Zap } from 'lucide-react';
import type { NostrEvent } from '@nostrify/nostrify';

import { LoginArea } from '@/components/auth/LoginArea';
import { useNostr } from '@nostrify/react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { discoverWebAuthor, resolveAuthorInput, suggestDomainIdentity } from '@/lib/discoverWebAuthor';
import { APP_WRITE_RELAY_URLS } from '@/lib/appRelays';
import { getDisplayName } from '@/lib/getDisplayName';
import { HIGHLIGHT_KIND } from '@/lib/highlights';
import { normalizeUrl } from '@/lib/normalizeUrl';
import { sanitizeUrl } from '@/lib/sanitizeUrl';

/** Where the highlight points at. `web` = URL source (optionally pre-filled). */
export type ComposerSource =
  | { type: 'article'; coordinate: string; authorPubkey: string; label: string }
  | { type: 'note'; id: string; authorPubkey: string; label: string }
  | { type: 'web'; url?: string };

interface HighlightComposerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: ComposerSource;
  /** Pre-filled quote from a text selection. When set, the quote is read-only. */
  initialQuote?: string;
  /** Surrounding paragraph, included as the NIP-84 `context` tag. */
  initialContext?: string;
  /** Pre-filled URL for web sources (defaults to source.url when set). */
  initialUrl?: string;
  onPublished?: (event: NostrEvent) => void;
}

const MAX_QUOTE_LENGTH = 10_000;
const MAX_COMMENT_LENGTH = 5_000;

/** Small avatar+name chip for the credited author. */
function AuthorChip({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const name = getDisplayName(author.data?.metadata, pubkey);
  const picture = sanitizeUrl(author.data?.metadata?.picture);

  return (
    <span className="inline-flex min-w-0 items-center gap-1.5 font-medium">
      <Avatar className="size-4 border border-border">
        {picture && <AvatarImage src={picture} alt="" />}
        <AvatarFallback className="bg-secondary text-[8px] font-semibold">
          {name.slice(0, 2).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <span className="truncate">{name}</span>
    </span>
  );
}

function SourceLine({ source }: { source: ComposerSource }) {
  const icon =
    source.type === 'article' ? (
      <BookOpenText className="size-4 shrink-0 text-primary" />
    ) : source.type === 'note' ? (
      <MessageSquareQuote className="size-4 shrink-0 text-primary" />
    ) : (
      <Globe className="size-4 shrink-0 text-primary" />
    );

  return (
    <span className="inline-flex items-center gap-1.5">
      {icon}
      {source.type === 'web' ? 'A passage from the web' : source.label}
    </span>
  );
}

/** Dialog for publishing a NIP-84 highlight. */
export function HighlightComposer({
  open,
  onOpenChange,
  source,
  initialQuote,
  initialContext,
  initialUrl,
  onPublished,
}: HighlightComposerProps) {
  const { user } = useCurrentUser();
  const { nostr } = useNostr();
  const { mutateAsync: createEvent, isPending } = useNostrPublish();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const effectiveInitialUrl = initialUrl ?? (source.type === 'web' ? source.url : undefined);

  const [quote, setQuote] = useState(initialQuote ?? '');
  const [url, setUrl] = useState(effectiveInitialUrl ?? '');
  const [comment, setComment] = useState('');

  // Web author attribution state.
  const [authorPubkey, setAuthorPubkey] = useState<string | null>(null);
  const [authorInput, setAuthorInput] = useState('');
  const [authorSearching, setAuthorSearching] = useState(false);
  const [authorInvalid, setAuthorInvalid] = useState(false);
  /** Domain-level identity suggestion (_@domain) — shown, never auto-applied. */
  const [domainSuggestion, setDomainSuggestion] = useState<string | null>(null);
  /** After publishing an uncredited web highlight: the author-invite view. */
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);

  const sanitizedUrl = useMemo(
    () => (url.trim() ? sanitizeUrl(url.trim()) : undefined),
    [url],
  );

  // Reset fields each time the dialog opens with fresh values.
  useEffect(() => {
    if (open) {
      setQuote(initialQuote ?? '');
      setUrl(effectiveInitialUrl ?? '');
      setComment('');
      setAuthorPubkey(null);
      setAuthorInput('');
      setAuthorSearching(false);
      setAuthorInvalid(false);
      setDomainSuggestion(null);
      setInviteUrl(null);
    }
  }, [open, initialQuote, effectiveInitialUrl]);

  // Changing the URL resets author attribution.
  useEffect(() => {
    setAuthorPubkey(null);
    setAuthorInput('');
    setAuthorInvalid(false);
    setDomainSuggestion(null);
  }, [sanitizedUrl]);

  // Auto-discover the page author's Nostr identity (rel=me), debounced.
  // Falls back to a domain-level (_@domain) *suggestion*.
  useEffect(() => {
    if (source.type !== 'web' || !sanitizedUrl || !open) return;

    let cancelled = false;
    setAuthorSearching(true);
    const timer = setTimeout(async () => {
      const found = await discoverWebAuthor(sanitizedUrl);
      if (cancelled) return;
      if (found) {
        setAuthorSearching(false);
        setAuthorPubkey(found);
        return;
      }
      const suggestion = await suggestDomainIdentity(sanitizedUrl);
      if (cancelled) return;
      setAuthorSearching(false);
      setDomainSuggestion(suggestion);
    }, 600);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [sanitizedUrl, source.type, open]);

  // Resolve a manually entered author identity (npub / nprofile / hex / NIP-05).
  useEffect(() => {
    const input = authorInput.trim();
    if (!input) {
      setAuthorInvalid(false);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      const found = await resolveAuthorInput(input);
      if (cancelled) return;
      if (found) {
        setAuthorPubkey(found);
        setAuthorInput('');
        setAuthorInvalid(false);
      } else {
        setAuthorInvalid(true);
      }
    }, 700);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [authorInput]);

  const trimmedQuote = quote.trim();
  const quoteValid = trimmedQuote.length >= 2 && trimmedQuote.length <= MAX_QUOTE_LENGTH;
  const urlInvalid = url.trim().length > 0 && !sanitizedUrl;
  const commentValid = comment.length <= MAX_COMMENT_LENGTH;
  const canPublish = quoteValid && !urlInvalid && commentValid && !isPending;

  // True when this highlight will carry author/highlighter zap split tags.
  const splitsZaps = user
    ? source.type === 'article' || source.type === 'note'
      ? source.authorPubkey !== user.pubkey
      : !!authorPubkey && authorPubkey !== user.pubkey
    : false;

  const inviteMessage = inviteUrl
    ? `I highlighted a passage from your page on Nostr:\n\n${window.location.origin}/web/${encodeURIComponent(inviteUrl)}\n\nReaders can tip you in Bitcoin for passages from your site — you just need a Nostr identity declared with one <link rel="me" href="nostr:npub1…"> tag. Highlights of your work will then credit (and pay) you automatically.`
    : '';

  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteMessage);
      toast({ title: 'Invite copied', description: 'Send it to the author wherever you can reach them.' });
    } catch {
      toast({ title: 'Copy failed', description: 'Select the message and copy it manually.', variant: 'destructive' });
    }
    onOpenChange(false);
  };

  const publish = async () => {
    if (!canPublish || !user) return;

    const tags: string[][] = [];

    if (source.type === 'article' || source.type === 'note') {
      tags.push(source.type === 'article' ? ['a', source.coordinate] : ['e', source.id]);
      tags.push(['p', source.authorPubkey, '', 'author']);

      // NIP-57 zap splits: zaps on this highlight should primarily pay the
      // original author, with a share for the highlighter's curation.
      if (source.authorPubkey !== user.pubkey) {
        tags.push(['zap', source.authorPubkey, '', '3']);
        tags.push(['zap', user.pubkey, '', '1']);
      }
    } else if (sanitizedUrl) {
      // Canonicalize so highlights of the same page group together (NIP-84).
      tags.push(['r', normalizeUrl(sanitizedUrl) ?? sanitizedUrl, '', 'source']);

      // Credit the web author when their Nostr identity is known.
      if (authorPubkey) {
        tags.push(['p', authorPubkey, '', 'author']);
        if (authorPubkey !== user.pubkey) {
          tags.push(['zap', authorPubkey, '', '3']);
          tags.push(['zap', user.pubkey, '', '1']);
        }
      }
    }

    if (initialContext) {
      tags.push(['context', initialContext]);
    }

    const trimmedComment = comment.trim();
    if (trimmedComment) {
      tags.push(['comment', trimmedComment]);
    }

    try {
      const event = await createEvent({
        kind: HIGHLIGHT_KIND,
        content: trimmedQuote,
        tags,
      });

      toast({ title: 'Highlight published', description: 'Your highlight is live on your relays.' });
      onPublished?.(event);
      queryClient.invalidateQueries({ queryKey: ['nostr', 'highlights-feed'] });
      queryClient.invalidateQueries({ queryKey: ['nostr', 'highlights-author'] });

      // Rebroadcast to glowmark's relays so embeds and permalinks hydrate
      // for anonymous viewers regardless of the author's own relay set.
      nostr.group(APP_WRITE_RELAY_URLS).event(event).catch(() => {
        // Best-effort — the highlight already lives on the author's relays.
      });

      // Uncredited web highlight → offer to invite the author onto Nostr.
      if (source.type === 'web' && sanitizedUrl && !authorPubkey) {
        setInviteUrl(sanitizedUrl);
      } else {
        onOpenChange(false);
      }
    } catch {
      toast({
        title: 'Failed to publish',
        description: 'Your relays may be unreachable. Try again in a moment.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Highlighter className="size-4.5 text-primary" />
            New highlight
          </DialogTitle>
          <DialogDescription asChild>
            <div className="pt-1">
              <SourceLine source={source} />
            </div>
          </DialogDescription>
        </DialogHeader>

        {inviteUrl ? (
          /* Post-publish: invite the (uncredited) author onto Nostr */
          <div className="space-y-4">
            <p className="text-sm leading-relaxed">
              Your highlight is live. The page didn't declare a Nostr identity, so the author
              isn't credited — and can't receive zaps — yet.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Know them? This message tells them how to become payable:
            </p>
            <Textarea
              readOnly
              value={inviteMessage}
              rows={6}
              className="text-xs"
              onFocus={(e) => e.target.select()}
              aria-label="Invite message"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Skip
              </Button>
              <Button onClick={copyInvite}>
                <Copy className="size-4" />
                Copy invite message
              </Button>
            </div>
          </div>
        ) : !user ? (
          <div className="py-4 flex flex-col items-center gap-4 text-center">
            <p className="text-sm text-muted-foreground max-w-xs">
              Log in with your Nostr key to publish highlights.
            </p>
            <LoginArea className="max-w-44" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="highlight-quote">Passage</Label>
              {initialQuote ? (
                <blockquote className="border-l-4 border-primary/70 pl-4 py-1 font-serif leading-relaxed max-h-40 overflow-y-auto">
                  {initialQuote}
                </blockquote>
              ) : (
                <Textarea
                  id="highlight-quote"
                  value={quote}
                  onChange={(e) => setQuote(e.target.value)}
                  placeholder="Paste the passage worth remembering…"
                  rows={4}
                  className="font-serif"
                />
              )}
            </div>

            {source.type === 'web' && (
              <div className="space-y-2">
                <Label htmlFor="highlight-url">Source URL</Label>
                <Input
                  id="highlight-url"
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com/article"
                  aria-invalid={urlInvalid}
                />
                {urlInvalid ? (
                  <p className="text-xs text-destructive">Enter a valid https:// URL.</p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Optional, but linking the source helps others find the original.
                  </p>
                )}

                {/* Web author attribution */}
                {sanitizedUrl && (
                  <div className="pt-1">
                    {authorPubkey ? (
                      <div className="flex items-center gap-2 rounded-md border border-primary/40 bg-accent/60 px-2.5 py-1.5 text-xs">
                        <BadgeCheck className="size-3.5 shrink-0 text-primary" />
                        <AuthorChip pubkey={authorPubkey} />
                        <span className="text-muted-foreground">credited — receives zaps</span>
                        <button
                          type="button"
                          onClick={() => setAuthorPubkey(null)}
                          aria-label="Remove credited author"
                          className="ml-auto rounded-sm p-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <X className="size-3.5" />
                        </button>
                      </div>
                    ) : authorSearching ? (
                      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Loader2 className="size-3.5 animate-spin" />
                        Looking for the author's Nostr identity…
                      </p>
                    ) : (
                      <div className="space-y-1.5">
                        {domainSuggestion && (
                          <div className="flex items-center gap-2 rounded-md border border-border bg-muted/50 px-2.5 py-1.5 text-xs">
                            <AuthorChip pubkey={domainSuggestion} />
                            <span className="text-muted-foreground">found via this domain</span>
                            <Button
                              size="sm"
                              variant="secondary"
                              className="h-6 px-2 text-xs ml-auto"
                              onClick={() => {
                                setAuthorPubkey(domainSuggestion);
                                setDomainSuggestion(null);
                              }}
                            >
                              Credit
                            </Button>
                            <button
                              type="button"
                              onClick={() => setDomainSuggestion(null)}
                              aria-label="Dismiss suggestion"
                              className="rounded-sm p-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              <X className="size-3.5" />
                            </button>
                          </div>
                        )}
                        <Label htmlFor="highlight-author" className="text-xs text-muted-foreground">
                          Author's Nostr ID <span className="font-normal">(optional)</span>
                        </Label>
                        <Input
                          id="highlight-author"
                          value={authorInput}
                          onChange={(e) => setAuthorInput(e.target.value)}
                          placeholder="npub1… or name@domain"
                          className="h-8 text-sm"
                          aria-invalid={authorInvalid}
                        />
                        {authorInvalid ? (
                          <p className="text-xs text-destructive">Couldn't resolve that identity.</p>
                        ) : (
                          <p className="text-xs text-muted-foreground">
                            Pages declaring a nostr identity (rel=&quot;me&quot;) are detected
                            automatically. Author claims are re-verified against the page before
                            zaps are paid — false credits don't get money.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {initialContext && (
              <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3 border border-border/60 rounded-md bg-muted/40 px-3 py-2">
                <span className="font-medium">Context:</span> {initialContext}
              </p>
            )}

            <div className="space-y-2">
              <Label htmlFor="highlight-comment">
                Comment <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <Textarea
                id="highlight-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Why did this passage stop you?"
                rows={2}
              />
              <p className="text-xs text-muted-foreground">
                Adding a comment turns this into a quote highlight.
              </p>
            </div>

            {splitsZaps && (
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Zap className="size-3.5 text-primary" />
                Zaps on this highlight split 75% to the author, 25% to you.
              </p>
            )}
          </div>
        )}

        {user && !inviteUrl && (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
              Cancel
            </Button>
            <Button onClick={publish} disabled={!canPublish}>
              {isPending && <Loader2 className="size-4 animate-spin" />}
              Publish highlight
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
